import React, { useContext, createContext, useMemo } from 'react';
import { FileText, Loader2, AlertCircle, MapPin, X, Eye, EyeOff, HardDrive } from 'lucide-react';
import { useAsBuiltViewer, type BoundingBoxOverlay } from '../../hooks/useAsBuiltViewer';
import { useTheme } from '../../contexts/ThemeContext';

// PDF Viewer imports
import { Viewer, Worker, SpecialZoomLevel, type RenderPage, type RenderPageProps, type SetRenderRange } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import '@react-pdf-viewer/page-navigation/lib/styles/index.css';

// Context so bbox overlays can re-render independently of the PDF viewer
interface BBoxContextValue {
  boundingBoxMap: Record<number, BoundingBoxOverlay[]>;
  showBoundingBoxes: boolean;
  highlightedBBox: { pageNumber: number; bbox: { x_min: number; y_min: number; x_max: number; y_max: number } } | null;
}

const BBoxContext = createContext<BBoxContextValue>({
  boundingBoxMap: {},
  showBoundingBoxes: true,
  highlightedBBox: null,
});

const BBOX_SCALE = 1000;

// Render all page containers so jumpToPage never scrolls to a blank area.
// This is lightweight: only the container divs (positioned placeholders) are created for
// every page. Actual canvas/text rendering is managed by the library's render queue
// which processes one page at a time based on visibility priority.
const renderAllPages: SetRenderRange = (visiblePagesRange) => ({
  startPage: 0,
  endPage: visiblePagesRange.numPages - 1,
});

/** Renders bbox overlays for a single page. Re-renders via context, not renderPage. */
const BBoxPageOverlay: React.FC<{ pageNumber: number; width: number; height: number }> = React.memo(
  ({ pageNumber, width, height }) => {
    const { boundingBoxMap, showBoundingBoxes, highlightedBBox } = useContext(BBoxContext);

    // Determine which boxes to render:
    // - showBoundingBoxes ON → all boxes on this page
    // - showBoundingBoxes OFF → only the highlighted (clicked) box, if on this page
    let boxes: BoundingBoxOverlay[] | undefined;
    if (showBoundingBoxes) {
      boxes = boundingBoxMap[pageNumber];
    } else if (highlightedBBox && highlightedBBox.pageNumber === pageNumber) {
      // Find the matching box from the map to get full metadata (answer_type etc.)
      const pageBoxes = boundingBoxMap[pageNumber];
      if (pageBoxes) {
        const match = pageBoxes.find(
          b => b.x_min === highlightedBBox.bbox.x_min &&
               b.y_min === highlightedBBox.bbox.y_min &&
               b.x_max === highlightedBBox.bbox.x_max &&
               b.y_max === highlightedBBox.bbox.y_max
        );
        if (match) boxes = [match];
      }
    }

    if (!boxes || boxes.length === 0) return null;

    return (
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: `${width}px`,
          height: `${height}px`,
          pointerEvents: 'none',
          zIndex: 5,
        }}
      >
        {boxes.map((box, i) => {
          const isHighlighted = highlightedBBox &&
            highlightedBBox.pageNumber === pageNumber &&
            highlightedBBox.bbox.x_min === box.x_min &&
            highlightedBBox.bbox.y_min === box.y_min &&
            highlightedBBox.bbox.x_max === box.x_max &&
            highlightedBBox.bbox.y_max === box.y_max;

          const borderColor =
            box.answer_type === 'fail' ? 'rgba(239, 68, 68, 0.85)' :
            box.answer_type === 'pass' ? 'rgba(34, 197, 94, 0.85)' :
            'rgba(234, 179, 8, 0.85)';
          const bgColor =
            box.answer_type === 'fail' ? 'rgba(239, 68, 68, 0.08)' :
            box.answer_type === 'pass' ? 'rgba(34, 197, 94, 0.08)' :
            'rgba(234, 179, 8, 0.08)';

          return (
            <div
              key={`bbox-${pageNumber}-${i}`}
              className={isHighlighted ? 'bbox-highlight-pulse' : ''}
              style={{
                position: 'absolute',
                left: `${(box.x_min / BBOX_SCALE) * width}px`,
                top: `${(box.y_min / BBOX_SCALE) * height}px`,
                width: `${((box.x_max - box.x_min) / BBOX_SCALE) * width}px`,
                height: `${((box.y_max - box.y_min) / BBOX_SCALE) * height}px`,
                border: isHighlighted ? `3px solid ${borderColor}` : `2px solid ${borderColor}`,
                backgroundColor: isHighlighted ? (
                  box.answer_type === 'fail' ? 'rgba(239, 68, 68, 0.25)' :
                  box.answer_type === 'pass' ? 'rgba(34, 197, 94, 0.25)' :
                  'rgba(234, 179, 8, 0.25)'
                ) : bgColor,
                borderRadius: '2px',
                pointerEvents: 'none',
                boxShadow: isHighlighted ? `0 0 12px 4px ${borderColor}` : 'none',
              }}
              title={box.description || `${box.answer_type} - ${box.category || 'Q' + box.question_id}`}
            />
          );
        })}
      </div>
    );
  }
);

// Stable renderPage — never changes reference, so the PDF viewer never re-renders due to bbox updates
const stableRenderPage: RenderPage = (props: RenderPageProps) => {
  const { pageIndex, width, height, canvasLayer, textLayer, annotationLayer } = props;
  const pageNumber = pageIndex + 1;

  return (
    <>
      {canvasLayer.children}
      {textLayer.children}
      {annotationLayer.children}
      <BBoxPageOverlay pageNumber={pageNumber} width={width} height={height} />
    </>
  );
};

const AsBuiltViewerTab: React.FC = () => {
  const { resolvedTheme } = useTheme();
  const {
    currentSite,
    tenantId,
    siteId,
    asBuiltArtefact,
    artefactsError,
    pdfViewUrl,
    viewerError,
    isFromCache,
    downloadProgress,
    displayPage,
    goToPage,
    selectedComponent,
    clearSelectedComponent,
    handlePageChange,
    handleDocumentLoad,
    defaultLayoutPluginInstance,
    pageNavigationPluginInstance,
    boundingBoxMap,
    showBoundingBoxes,
    toggleBoundingBoxes,
    isFetchingBBoxes,
    highlightedBBox,
    isLoading,
  } = useAsBuiltViewer();

  // Stable plugins array — prevents Viewer's internal renderViewer from being
  // recreated on every render (its deps include [plugins, virtualizer])
  const plugins = useMemo(
    () => [defaultLayoutPluginInstance, pageNavigationPluginInstance],
    [defaultLayoutPluginInstance, pageNavigationPluginInstance]
  );

  // Context value — only this object changes, not renderPage
  const bboxContextValue = React.useMemo<BBoxContextValue>(() => ({
    boundingBoxMap,
    showBoundingBoxes,
    highlightedBBox,
  }), [boundingBoxMap, showBoundingBoxes, highlightedBBox]);

  // No site selected
  if (!currentSite || !tenantId || !siteId) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-[var(--color-bg-primary)]">
        <div className="text-center p-8">
          <FileText className="w-12 h-12 eg-text-secondary mx-auto mb-4" />
          <h2 className="text-xl font-semibold eg-text-primary mb-2">No Site Selected</h2>
          <p className="eg-text-secondary">
            Please select a site from the Sites List to view as-built drawings.
          </p>
        </div>
      </div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-[var(--color-bg-primary)]">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-engramiq-green animate-spin mx-auto mb-4" />
          <p className="eg-text-primary">Loading as-built drawings...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (artefactsError) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-[var(--color-bg-primary)]">
        <div className="text-center p-8">
          <AlertCircle className="w-12 h-12 text-accent-red mx-auto mb-4" />
          <h2 className="text-xl font-semibold eg-text-primary mb-2">Error Loading Artefacts</h2>
          <p className="eg-text-secondary">
            Failed to load as-built drawings. Please try again later.
          </p>
        </div>
      </div>
    );
  }

  // No as-built found
  if (!asBuiltArtefact) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-[var(--color-bg-primary)]">
        <div className="text-center p-8">
          <FileText className="w-12 h-12 eg-text-secondary mx-auto mb-4" />
          <h2 className="text-xl font-semibold eg-text-primary mb-2">No As-Built Drawing Found</h2>
          <p className="eg-text-secondary">
            No processed as-built drawing is available for this site.
          </p>
        </div>
      </div>
    );
  }

  return (
    <BBoxContext.Provider value={bboxContextValue}>
      <div className="w-full h-full bg-[var(--color-bg-primary)] flex flex-col">
        {/* PDF Viewer — flex-1 is the ONLY flex child so its height never changes */}
        <div className="flex-1 w-full relative overflow-hidden">
          {/* Selected Component Page Refs Indicator — absolute overlay to avoid resizing the viewer container */}
          {selectedComponent && selectedComponent.pageRefs.length > 0 && (
            <div className="absolute top-0 left-0 right-0 z-20 px-2 py-1 bg-engramiq-green/10 backdrop-blur-sm border-b border-[var(--color-border)] flex items-center gap-2">
              <MapPin className="w-3.5 h-3.5 text-engramiq-green flex-shrink-0" />
              <span className="text-xs eg-text-primary font-medium truncate max-w-[150px]">
                {selectedComponent.nodeLabel || selectedComponent.nodeId}:
              </span>
              <div className="flex items-center gap-1.5 flex-wrap">
                {selectedComponent.pageRefs.map((ref, idx) => (
                  <button
                    type="button"
                    key={idx}
                    onClick={() => goToPage(ref.page)}
                    className={`px-2 py-0.5 text-xs rounded-full transition-colors ${displayPage === ref.page
                        ? 'bg-engramiq-green text-engramiq-dark-blue font-semibold'
                        : 'bg-[var(--color-surface)] eg-text-primary hover:bg-[var(--color-surface-hover)]'
                      }`}
                  >
                    p{ref.page}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={clearSelectedComponent}
                className="ml-auto p-0.5 eg-text-secondary hover:text-[var(--color-text-primary)] hover:bg-[var(--color-surface-hover)] rounded"
                title="Clear selection"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
          {viewerError ? (
            <div className="absolute inset-0 flex items-center justify-center bg-[var(--color-surface)]">
              <div className="text-center">
                <AlertCircle className="w-8 h-8 text-accent-red mx-auto mb-2" />
                <p className="eg-text-primary mb-1">Cannot Display Document</p>
                <p className="eg-text-secondary text-sm mb-3">{viewerError}</p>
              </div>
            </div>
          ) : pdfViewUrl ? (
            <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
              <div className={`h-full pdf-viewer-${resolvedTheme}
                [&_.rpv-core__viewer]:h-full
                [&_.rpv-core__inner-container]:!bg-[var(--color-surface)]
                [&_.rpv-default-layout__container]:!bg-[var(--color-surface)]
                [&_.rpv-default-layout__toolbar]:!bg-[var(--color-bg-primary)]
                [&_.rpv-default-layout__toolbar]:!border-b
                [&_.rpv-default-layout__toolbar]:!border-[var(--color-border)]
                [&_.rpv-core__minimal-button]:!text-[var(--color-text-primary)]
                [&_.rpv-core__minimal-button:hover]:!bg-[var(--color-surface-hover)]
                [&_.rpv-core__textbox]:!bg-[var(--color-surface)]
                [&_.rpv-core__textbox]:!text-[var(--color-text-primary)]
                [&_.rpv-core__textbox]:!border-[var(--color-border)]
                [&_.rpv-zoom__popover-target-scale]:!text-[var(--color-text-primary)]
                [&_.rpv-page-navigation__current-page-input]:!bg-[var(--color-surface)]
                [&_.rpv-page-navigation__current-page-input]:!text-[var(--color-text-primary)]
                [&_.rpv-open__button]:!hidden
                [&_button[aria-label*='Open']]:!hidden
                [&_button[title*='Open']]:!hidden
              `}>
                <Viewer
                  fileUrl={pdfViewUrl}
                  plugins={plugins}
                  defaultScale={SpecialZoomLevel.PageWidth}
                  onPageChange={handlePageChange}
                  onDocumentLoad={handleDocumentLoad}
                  theme={resolvedTheme}
                  renderPage={stableRenderPage}
                  setRenderRange={renderAllPages}
                  renderError={() => (
                    <div className="flex items-center justify-center h-full">
                      <div className="text-center">
                        <AlertCircle className="w-8 h-8 text-accent-red mx-auto mb-2" />
                        <p className="eg-text-primary">Failed to load PDF</p>
                      </div>
                    </div>
                  )}
                />
              </div>
            </Worker>
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center w-64">
                <Loader2 className="w-8 h-8 text-engramiq-green animate-spin mx-auto mb-3" />
                {downloadProgress != null ? (
                  <>
                    <p className="text-sm eg-text-primary mb-2">Downloading PDF...</p>
                    <div className="w-full h-2 rounded-full bg-[var(--color-bg-tertiary)] overflow-hidden">
                      <div
                        className="h-full rounded-full bg-engramiq-green transition-all duration-200"
                        style={{ width: `${downloadProgress}%` }}
                      />
                    </div>
                    <p className="text-xs eg-text-secondary mt-1.5">{downloadProgress}%</p>
                  </>
                ) : (
                  <p className="text-sm eg-text-secondary">Loading document...</p>
                )}
              </div>
            </div>
          )}

          {/* Bottom controls */}
          {pdfViewUrl && (
            <div className="absolute bottom-4 right-4 z-20 flex items-center gap-2">
              {isFromCache && (
                <div
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-[var(--color-surface)] eg-text-secondary border border-[var(--color-border)] shadow-lg"
                  title="Loaded from local cache"
                >
                  <HardDrive className="w-3.5 h-3.5" />
                  Cached
                </div>
              )}
              <button
                onClick={toggleBoundingBoxes}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium shadow-lg transition-all ${
                  showBoundingBoxes
                    ? 'bg-engramiq-green/90 text-engramiq-dark-blue hover:bg-engramiq-green'
                    : 'bg-[var(--color-surface)] eg-text-secondary hover:bg-[var(--color-surface-hover)] border border-[var(--color-border)]'
                }`}
                title={showBoundingBoxes ? 'Hide all bounding boxes' : 'Show all bounding boxes'}
              >
                {showBoundingBoxes ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                {isFetchingBBoxes ? 'Loading...' : 'Show All Boxes'}
              </button>
            </div>
          )}
        </div>
      </div>
    </BBoxContext.Provider>
  );
};

export default AsBuiltViewerTab;
